"use client";

import { useEffect, useState } from "react";
import type { OrderOutput } from "@/interfaces";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { numberToMoney } from "@/util/moneyConversion";

interface CustomerDelinquency {
  name: string;
  amount: number;
  id: string;
}

interface DelinquencyByCustomerChartProps {
  orders: OrderOutput[];
}

export function DelinquencyByCustomerChart({
  orders,
}: DelinquencyByCustomerChartProps) {
  const [data, setData] = useState<CustomerDelinquency[]>([]);

  useEffect(() => {
    if (!orders?.length) {
      setData([]);
      return;
    }

    const customerMap = new Map<
      string,
      { name: string; amount: number; id: string }
    >();

    orders.forEach((order) => {
      if (!order.customer) return;

      const customerId =
        typeof order.customer === "string" ? order.customer : order.customer.id;
      const customerName =
        typeof order.customer === "string"
          ? "Cliente " + customerId.substring(0, 8)
          : order.customer.name || "Cliente sem nome";

      const paidAmount =
        order.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
      const pendingAmount = order.totalAmount - paidAmount;

      if (customerMap.has(customerId)) {
        const current = customerMap.get(customerId)!;
        customerMap.set(customerId, {
          ...current,
          amount: current.amount + pendingAmount,
        });
      } else {
        customerMap.set(customerId, {
          id: customerId,
          name: customerName,
          amount: pendingAmount,
        });
      }
    });

    // Convert to array and sort by amount
    const sortedData = Array.from(customerMap.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10); // Top 10 customers

    setData(sortedData);
  }, [orders]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-center">
          Não há dados de inadimplência por cliente para exibir.
        </p>
      </div>
    );
  }

  // Generate colors based on amount (higher amounts = more intense red)
  const getBarColor = (amount: number) => {
    const maxAmount = Math.max(...data.map((item) => item.amount));
    const intensity = Math.max(0.3, Math.min(0.9, amount / maxAmount));
    return `rgba(239, 68, 68, ${intensity})`;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          horizontal={true}
          vertical={false}
        />
        <XAxis
          type="number"
          tickFormatter={(value) => numberToMoney(value)}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={100}
          tickFormatter={(value) =>
            value.length > 12 ? `${value.substring(0, 12)}...` : value
          }
        />
        <Tooltip
          formatter={(value: number) => [
            numberToMoney(value),
            "Valor Pendente",
          ]}
          labelFormatter={(label) => `Cliente: ${label}`}
        />
        <Bar dataKey="amount" name="Valor Pendente">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getBarColor(entry.amount)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
